import type {
  ComplaintCategory,
  ComplaintConclusion,
  ComplaintDetail,
  ComplaintStatus,
  ComplaintWorkbenchQuery,
} from './complaints-api';

export const COMPLAINT_CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  COMPLIANCE_SAFETY: '合规安全',
  ENTERPRISE_TRAINING: '企业培训',
};

export const COMPLAINT_STATUS_LABELS: Record<ComplaintStatus, string> = {
  PENDING: '待处理',
  IN_PROGRESS: '处理中',
  NEED_MORE: '待补充',
  RESOLVED: '已完成',
  APPEAL_REVIEW: '申诉复核中',
};

export const COMPLAINT_CONCLUSION_LABELS: Record<ComplaintConclusion, string> = {
  ESTABLISHED: '成立',
  PARTIALLY_ESTABLISHED: '部分成立',
  NOT_ESTABLISHED: '不成立',
};

export type ComplaintActionMode = 'start' | 'need-more' | 'resolve' | 'appeal-review';

export interface ComplaintFilterDraft {
  keyword: string;
  category: ComplaintCategory | '';
  status: ComplaintStatus | '';
  conclusion: ComplaintConclusion | '';
  submitterEmployeeId: string;
  handlerEmployeeId: string;
  startDate: string;
  endDate: string;
  sortBy: NonNullable<ComplaintWorkbenchQuery['sortBy']>;
  sortOrder: NonNullable<ComplaintWorkbenchQuery['sortOrder']>;
}

export const DEFAULT_COMPLAINT_FILTERS: ComplaintFilterDraft = {
  keyword: '',
  category: '',
  status: '',
  conclusion: '',
  submitterEmployeeId: '',
  handlerEmployeeId: '',
  startDate: '',
  endDate: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

export function buildComplaintWorkbenchQuery(
  filters: ComplaintFilterDraft,
  page: number,
  pageSize: number,
): ComplaintWorkbenchQuery {
  return {
    keyword: filters.keyword.trim() || undefined,
    category: filters.category || undefined,
    status: filters.status || undefined,
    conclusion: filters.conclusion || undefined,
    submitterEmployeeId: filters.submitterEmployeeId.trim() || undefined,
    handlerEmployeeId: filters.handlerEmployeeId.trim() || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    page,
    pageSize,
  };
}

/** 列表只展示不可逆的短掩码；不把后端 employeeId 原样暴露给处理人。 */
export function maskEmployeeId(value: string | null | undefined): string {
  const normalized = value?.trim() ?? '';
  if (!normalized) return '—';
  if (normalized.length <= 4) return `${normalized.slice(0, 1)}***`;
  return `${normalized.slice(0, 2)}***${normalized.slice(-2)}`;
}

export function formatComplaintDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('zh-CN');
}

export function availableComplaintActions(detail: ComplaintDetail): ComplaintActionMode[] {
  if (detail.status === 'PENDING') return ['start'];
  if (detail.status === 'IN_PROGRESS') return ['need-more', 'resolve'];
  if (detail.status === 'APPEAL_REVIEW') return ['appeal-review'];
  return [];
}

export function complaintActionLabel(mode: ComplaintActionMode): string {
  if (mode === 'start') return '领取并开始处理';
  if (mode === 'need-more') return '要求补充材料';
  if (mode === 'resolve') return '完成处理';
  return '完成申诉复核';
}
